import { VISIBILITY_MESSAGE, VISIBILITY_PLEASE_MESSAGE } from '@sdk/apps/host';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';

interface PersistentIframeProps {
  src: string;
  cacheKey?: number;
  /** What the guest may do. Read once, when the frame is created: an app's own posture by default. */
  sandbox?: string;
  /** Which browser features the guest may ask for (camera, clipboard, …). Read once, like `sandbox`. */
  allow?: string;
  className?: string;
  testId?: string;
  onLoad?: () => void;
  onError?: (error: Error) => void;
}

export interface PersistentIframeHandle {
  refresh: () => void;
  /** Post a message to the guest document (parent→iframe channel). */
  postToGuest: (message: unknown) => void;
  /** Whether a `message` event came from this frame's guest document (iframe→parent channel). */
  isGuest: (source: MessageEventSource | null) => boolean;
}

type IframeOwner = symbol;

// A parked frame stays mounted (so it never reloads) but must be `invisible`,
// not just `opacity-0 pointer-events-none`. The frame is cross-origin, so
// Chromium hit-tests wheel scrolling against its surface and ignores
// `pointer-events: none` there: an invisible parked frame left over from a web
// tab swallowed every wheel event over the content area it last covered, so no
// document, list or panel under it could scroll. `visibility: hidden` takes the
// frame out of hit-testing entirely; `visibility` is in the transition so the
// fade-out still plays before it flips.
const PARKED_CLASS = 'absolute invisible opacity-0 pointer-events-none transition-[opacity,visibility] duration-200';
const SHOWN_CLASS = 'absolute visible opacity-100 pointer-events-auto transition-[opacity,visibility] duration-200';

// A served app runs on its own origin with full browser powers.
const APP_SANDBOX =
  'allow-scripts allow-same-origin allow-forms allow-modals allow-orientation-lock allow-pointer-lock allow-presentation allow-popups-to-escape-sandbox allow-popups allow-downloads allow-storage-access-by-user-activation';
const APP_ALLOW =
  'accelerometer; autoplay; camera; encrypted-media; fullscreen; geolocation; gyroscope; microphone; midi; clipboard-read; clipboard-write; payment; usb; xr-spatial-tracking; screen-wake-lock; magnetometer; gamepad; picture-in-picture; display-capture';

// Global iframe registry that keeps iframes in fixed DOM locations
class IframeRegistry {
  private static instance: IframeRegistry;
  private iframes = new Map<string, HTMLIFrameElement>();
  private containers = new Map<string, HTMLDivElement>();
  private loadingStates = new Map<string, boolean>();
  private errorStates = new Map<string, boolean>();
  private ownerTargets = new Map<string, Map<IframeOwner, HTMLElement>>();
  private activeOwners = new Map<string, IframeOwner>();
  private resizeObservers = new Map<string, ResizeObserver>();
  private positionCleanups = new Map<string, () => void>();
  // The cache key each frame was last loaded under. Kept HERE, not in the component: a display that
  // unmounts its frame while the app is down remounts it with the key already advanced, and a
  // component-local "last key" would then see no change and re-reveal the parked error page.
  private loadedKeys = new Map<string, unknown>();

  static getInstance(): IframeRegistry {
    if (!IframeRegistry.instance) {
      IframeRegistry.instance = new IframeRegistry();
    }
    return IframeRegistry.instance;
  }

  private constructor() {
    // A guest that just loaded asks whether it is on screen: a notice sent before it listened is
    // gone. Answered for its own frame only (`isGuest`). Here, not in the display component: a
    // parked frame has no component mounted to answer for it.
    window.addEventListener('message', (event: MessageEvent) => {
      if ((event.data as { type?: string } | null)?.type !== VISIBILITY_PLEASE_MESSAGE) return;
      for (const src of this.iframes.keys()) {
        if (this.isGuest(src, event.source)) this.tellVisibility(src);
      }
    });
  }

  // A parked frame is hidden with CSS, which its guest cannot see; the SDK folds this notice into
  // the guest's own `document.hidden` (`adoptHostVisibility` in `@sdk/apps/host`).
  private tellVisibility(src: string): void {
    this.postToGuest(src, { type: VISIBILITY_MESSAGE, hidden: this.containers.get(src)?.className !== SHOWN_CLASS });
  }

  /**
   * A `testId` names the frame that is CURRENTLY on screen, so exactly one
   * iframe may carry it.
   *
   * The registry is keyed by `src` and deliberately never destroys an entry —
   * it parks dead frames so an app that recovers does not re-reveal Chrome's
   * error page. The consequence is that every distinct `src` for the same slot
   * (a reload nonce, a re-show that rebuilds the URL) leaves another parked
   * iframe behind, and each was stamped with the same `data-testid`. The
   * parked ones stay in the body portal forever, so the testid accumulates
   * duplicates: `[data-testid="vibe-app-frame"]` resolved to two elements, one
   * of them a retired frame nobody can see.
   *
   * Stripping the id from the losers keeps parking intact (the frame, its load
   * state and its guest document are untouched) while making the id mean what
   * it says. A parked frame that becomes current again is re-stamped by
   * `getOrCreateIframe`, which claims it back.
   */
  private claimTestId(testId: string, owner: HTMLIFrameElement): void {
    for (const frame of this.iframes.values()) {
      if (frame !== owner && frame.dataset.testid === testId) delete frame.dataset.testid;
    }
  }

  private createPortalContainer(): HTMLDivElement {
    const container = document.createElement('div');
    container.className = 'fixed top-0 left-0 pointer-events-none';
    container.style.width = '100vw';
    container.style.height = '100vh';
    document.body.appendChild(container);
    return container;
  }

  getOrCreateIframe(
    src: string,
    config: {
      onLoad?: () => void;
      onError?: (error: Error) => void;
      testId?: string;
      sandbox?: string;
      allow?: string;
    },
  ): { iframe: HTMLIFrameElement; container: HTMLDivElement } {
    const key = src;

    if (!this.iframes.has(key)) {
      // Create portal container that stays in a fixed DOM location
      const container = this.createPortalContainer();

      // Create iframe wrapper
      const iframeWrapper = document.createElement('div');
      iframeWrapper.className = PARKED_CLASS;

      // Create iframe
      const iframe = document.createElement('iframe');
      iframe.className = 'w-full h-full border-0';
      if (config.testId) iframe.dataset.testid = config.testId;
      iframe.setAttribute('sandbox', config.sandbox ?? APP_SANDBOX);
      const allow = config.allow ?? APP_ALLOW;
      if (allow) iframe.allow = allow;

      // Set initial loading state
      this.loadingStates.set(key, true);
      this.errorStates.set(key, false);

      const loadTimeout = setTimeout(() => {
        console.warn('iframe load timeout', key);
        this.loadingStates.set(key, false);
        this.errorStates.set(key, true);
        config.onError?.(new Error(`Failed to load iframe: ${src}`));
      }, 20000);

      // Add event listeners
      iframe.onload = () => {
        clearTimeout(loadTimeout);
        this.loadingStates.set(key, false);
        this.errorStates.set(key, false);
        config.onLoad?.();
      };

      iframe.onerror = () => {
        console.warn('iframe error', key);
        clearTimeout(loadTimeout);
        this.loadingStates.set(key, false);
        this.errorStates.set(key, true);
        config.onError?.(new Error(`Failed to load iframe: ${src}`));
      };

      iframe.src = src;

      iframeWrapper.appendChild(iframe);
      container.appendChild(iframeWrapper);

      this.iframes.set(key, iframe);
      this.containers.set(key, iframeWrapper);
      if (config.testId) this.claimTestId(config.testId, iframe);
    } else if (config.testId) {
      const iframe = this.iframes.get(key)!;
      iframe.dataset.testid = config.testId;
      this.claimTestId(config.testId, iframe);
    }

    return {
      iframe: this.iframes.get(key)!,
      container: this.containers.get(key)!,
    };
  }

  private updateIframePosition(src: string, targetElement: HTMLElement) {
    const container = this.containers.get(src);
    if (!container) return;

    const rect = targetElement.getBoundingClientRect();
    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;

    container.style.left = `${rect.left + scrollX}px`;
    container.style.top = `${rect.top + scrollY}px`;
    container.style.width = `${rect.width}px`;
    container.style.height = `${rect.height}px`;
  }

  private clearPositionTracking(src: string): void {
    const resizeObserver = this.resizeObservers.get(src);
    if (resizeObserver) {
      resizeObserver.disconnect();
      this.resizeObservers.delete(src);
    }

    this.positionCleanups.get(src)?.();
    this.positionCleanups.delete(src);
  }

  private activateIframe(src: string, targetElement: HTMLElement, owner: IframeOwner): boolean {
    const container = this.containers.get(src);

    if (container && targetElement) {
      this.activeOwners.set(src, owner);
      this.clearPositionTracking(src);

      // Position iframe over the target
      this.updateIframePosition(src, targetElement);

      // Show iframe
      container.className = SHOWN_CLASS;
      this.tellVisibility(src);

      // Set up resize observer to keep position updated
      const resizeObserver = new ResizeObserver(() => {
        if (this.activeOwners.get(src) === owner) {
          this.updateIframePosition(src, targetElement);
        }
      });

      resizeObserver.observe(targetElement);
      resizeObserver.observe(document.body); // Watch for layout changes
      this.resizeObservers.set(src, resizeObserver);

      // Update position on scroll
      const handleScroll = () => {
        if (this.activeOwners.get(src) === owner) {
          this.updateIframePosition(src, targetElement);
        }
      };
      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', handleScroll, { passive: true });

      this.positionCleanups.set(src, () => {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', handleScroll);
      });

      return true;
    }
    return false;
  }

  showIframeAt(src: string, targetElement: HTMLElement, owner: IframeOwner): boolean {
    this.getOrCreateIframe(src, {});

    // Already active over this exact target — the observers/listeners are in
    // place and the position is current, so skip the teardown-and-rebuild.
    if (this.activeOwners.get(src) === owner && this.ownerTargets.get(src)?.get(owner) === targetElement) {
      return true;
    }

    const targets = this.ownerTargets.get(src) ?? new Map<IframeOwner, HTMLElement>();
    targets.delete(owner);
    targets.set(owner, targetElement);
    this.ownerTargets.set(src, targets);

    return this.activateIframe(src, targetElement, owner);
  }

  private hideContainer(src: string): void {
    const container = this.containers.get(src);
    if (container) {
      container.className = PARKED_CLASS;
      this.tellVisibility(src);
    }
    this.clearPositionTracking(src);
  }

  hideIframe(src: string, owner: IframeOwner): void {
    const targets = this.ownerTargets.get(src);
    const wasActiveOwner = this.activeOwners.get(src) === owner;

    targets?.delete(owner);
    if (targets?.size === 0) {
      this.ownerTargets.delete(src);
    }

    // A retiring component must not hide an iframe already claimed by its
    // same-URL replacement.
    if (!wasActiveOwner) return;

    const fallback = targets
      ? Array.from(targets.entries())
          .reverse()
          .find(([, target]) => target.isConnected)
      : undefined;
    if (fallback) {
      const [fallbackOwner, fallbackTarget] = fallback;
      this.activateIframe(src, fallbackTarget, fallbackOwner);
      return;
    }

    this.activeOwners.delete(src);
    this.hideContainer(src);
  }

  getLoadingState(src: string): boolean {
    return this.loadingStates.get(src) ?? false;
  }

  getErrorState(src: string): boolean {
    return this.errorStates.get(src) ?? false;
  }

  cleanup(src: string): void {
    this.ownerTargets.delete(src);
    this.activeOwners.delete(src);
    this.hideContainer(src);

    const container = this.containers.get(src);
    if (container?.parentElement) {
      container.parentElement.remove(); // Remove the portal container
    }

    this.containers.delete(src);
    this.iframes.delete(src);
    this.loadingStates.delete(src);
    this.errorStates.delete(src);
    this.loadedKeys.delete(src);
  }

  /** Reload the frame when it is asked for under a key other than the one it was loaded with. */
  syncKey(src: string, key: unknown): void {
    const known = this.loadedKeys.has(src);
    const stale = known && this.loadedKeys.get(src) !== key;
    this.loadedKeys.set(src, key);
    if (stale) this.refresh(src);
  }

  refresh(src: string): void {
    const iframe = this.iframes.get(src);
    if (iframe) {
      iframe.src = src;
    }
  }

  postToGuest(src: string, message: unknown): void {
    const iframe = this.iframes.get(src);
    iframe?.contentWindow?.postMessage(message, '*');
  }

  isGuest(src: string, source: MessageEventSource | null): boolean {
    return source != null && this.iframes.get(src)?.contentWindow === source;
  }
}

const registry = IframeRegistry.getInstance();

/**
 * The epoch each frame was last shown at, by `src`.
 *
 * The host re-keys its body to signal "something changed behind the same
 * address", which REMOUNTS this viewer — but the iframe registry parks frames by
 * `src` and hands the same document back, so a remount alone reloads nothing, and
 * component state cannot tell a new epoch from a tab coming back into view. Held
 * per `src` here: a new epoch reloads, the same epoch does not.
 */
const shownAtEpoch = new Map<string, number>();

export function reloadOnNewEpoch(src: string, epoch: number | undefined, reload: () => void): void {
  if (!src || epoch === undefined) return;
  const seen = shownAtEpoch.get(src);
  shownAtEpoch.set(src, epoch);
  if (seen !== undefined && seen !== epoch) reload();
}

/**
 * Free the frame kept for `src` — its guest document, its load state and its epoch. For the owner
 * of a view's lifetime (the app pool, when the view's tab closes); a display that merely unmounts
 * parks its frame instead.
 */
export function releaseFrame(src: string): void {
  registry.cleanup(src);
  shownAtEpoch.delete(src);
}

const PersistentIframe = forwardRef<PersistentIframeHandle, PersistentIframeProps>(
  ({ src, cacheKey, sandbox, allow, testId, onLoad, onError }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const ownerRef = useRef<IframeOwner>(Symbol('persistent-iframe-owner'));
    const [, forceUpdate] = useState({});

    // Force re-render to get updated loading/error states
    const triggerUpdate = useCallback(() => forceUpdate({}), []);

    // NOTE: there used to be a pre-flight availability probe here --
    // `fetch(src, { redirect: 'manual' })`, treating `status >= 400` as "not
    // available". It could never work: a cross-origin `src` answers opaquely, with
    // a status that is always 0 -- measured identical whether the dev server was
    // alive or refusing connections. It therefore reported "available"
    // unconditionally, and its `catch` for connection-refused was unreachable.
    // Availability is decided by the app's lifecycle record (`useAppLifecycle`), which the backend keeps
    // from its own probes. This component is purely the
    // mechanism: it mounts a frame and reports load state.
    const refreshIframe = useCallback(() => {
      registry.refresh(src);
    }, [src]);

    // Expose refresh + guest-post methods to parent via ref
    useImperativeHandle(
      ref,
      () => ({
        refresh: refreshIframe,
        postToGuest: (message: unknown) => registry.postToGuest(src, message),
        isGuest: (source: MessageEventSource | null) => registry.isGuest(src, source),
      }),
      [refreshIframe, src],
    );

    useEffect(() => {
      registry.syncKey(src, cacheKey);
    }, [cacheKey, src]);

    // Get current iframe states
    const isIframeLoading = registry.getLoadingState(src);
    const isIframeError = registry.getErrorState(src);

    const showIframe = useCallback(() => {
      if (containerRef.current && !isIframeError) {
        registry.showIframeAt(src, containerRef.current, ownerRef.current);
        triggerUpdate();
      }
    }, [src, isIframeError, triggerUpdate]);

    const hideIframe = useCallback(() => {
      registry.hideIframe(src, ownerRef.current);
      triggerUpdate();
    }, [src, triggerUpdate]);

    // Initialize iframe when component mounts
    useEffect(() => {
      const config = {
        onLoad: () => {
          onLoad?.();
          triggerUpdate();
        },
        onError: (error: Error) => {
          onError?.(error);
          triggerUpdate();
        },
        testId,
        sandbox,
        allow,
      };

      registry.getOrCreateIframe(src, config);
    }, [src, onLoad, onError, testId, sandbox, allow, triggerUpdate]);

    // Auto-show iframe when ready and container is available
    useEffect(() => {
      if (!isIframeError && !isIframeLoading && containerRef.current) {
        showIframe();
      }
    }, [isIframeError, isIframeLoading, showIframe]);

    // Hide iframe when there's an error
    useEffect(() => {
      if (isIframeError) {
        hideIframe();
      }
    }, [isIframeError, hideIframe]);

    // Handle component unmount
    useEffect(() => {
      return () => {
        hideIframe();
      };
    }, [hideIframe]);

    // Renders nothing of its own: this component is the MECHANISM (a registry-
    // parked iframe positioned over this slot), not a surface. Loading and error
    // presentation belong to `WebappDisplay`, which is the only thing that can
    // actually tell whether the guest is healthy — a cross-origin frame reports
    // a refused navigation as a successful `onload`, so anything decided here
    // would be guesswork competing with the real verdict.
    return (
      <div className="relative h-full w-full" ref={containerRef} data-testid={testId ? `${testId}-host` : undefined} />
    );
  },
);

PersistentIframe.displayName = 'PersistentIframe';

export default PersistentIframe;
